import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import { Sidebar } from './layout';
import {
  SignInPage,
  HomePage,
  ListParserPage,
  ListParserDataPage,
  PartsPage,
  PartDetailsPage,
  FulfillmentPage,
  LabelPrintingPage,
  SalesOrderTrackerPage,
  PurchaseOrderTrackerPage,
  ProductionSchedulePage,
  JobTrackerPage,
  PictureProcessorPage,
  PictureManagerPage,
  PrinterAdminPage,
  QuoteDashboardPage,
  SalesAgentPage,
  QuoteTrackerPage,
  CustomerDashboardPage,
  QuoteToCashPage,
  PartDemandPage,
  DocsHomePage,
  DocsProcessPage,
  DocsProcessEditPage,
  DocsProcedurePage,
  DocsProcedureEditPage,
  DocsReviewQueuePage,
  DocsSearchPage,
  DocsImportPage,
  DocsAdminPage,
} from './pages';
import { useAuth } from './hooks/useAuth';
import { LoadingSpinner } from './components/LoadingSpinner';
import AuthCallback from './components/auth/AuthCallback';
import { ADMIN_CLEARANCE_LEVEL } from './services/types';

// Inner component to use useLocation hook
const MainContent: React.FC<{ signOut: () => void; isAdmin: boolean }> = ({
  signOut,
  isAdmin,
}) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar onSignOut={signOut} isAdmin={isAdmin} />
      <main
        className={`transition-all duration-300 ${
          isHomePage ? 'lg:pl-64' : 'lg:pl-20'
        }`}
      >
        <Routes>
          <Route path="/" element={<HomePage isAdmin={isAdmin} />} />
          <Route path="/list-parser" element={<ListParserPage />} />
          <Route path="/list-parser-data" element={<ListParserDataPage />} />
          <Route path="/label-printing" element={<LabelPrintingPage />} />
          <Route path="/parts" element={<PartsPage />} />
          <Route path="/parts/:partNum" element={<PartDetailsPage />} />
          <Route path="/part-demand" element={<PartDemandPage />} />
          <Route path="/part-demand/:partNum" element={<PartDemandPage />} />
          <Route path="/fulfillment" element={<FulfillmentPage />} />
          <Route path="/sales-orders" element={<SalesOrderTrackerPage />} />
          <Route
            path="/sales-orders/:orderNum"
            element={<SalesOrderTrackerPage />}
          />
          <Route
            path="/purchase-orders"
            element={<PurchaseOrderTrackerPage />}
          />
          <Route
            path="/purchase-orders/:poNum"
            element={<PurchaseOrderTrackerPage />}
          />
          <Route path="/job-tracker" element={<JobTrackerPage />} />
          <Route
            path="/production-schedule"
            element={<ProductionSchedulePage />}
          />
          <Route path="/picture-processor" element={<PictureProcessorPage />} />
          <Route path="/picture-manager" element={<PictureManagerPage />} />
          <Route path="/quote-dashboard" element={<QuoteDashboardPage />} />
          <Route path="/sales-agent" element={<SalesAgentPage />} />
          <Route path="/quotes" element={<QuoteTrackerPage />} />
          <Route path="/quotes/:quoteNum" element={<QuoteTrackerPage />} />
          <Route path="/customers" element={<CustomerDashboardPage />} />
          <Route path="/quote-to-cash" element={<QuoteToCashPage />} />
          <Route
            path="/customers/:custNum"
            element={<CustomerDashboardPage />}
          />
          {/* Docs routes */}
          <Route path="/docs" element={<DocsHomePage />} />
          <Route path="/docs/search" element={<DocsSearchPage />} />
          <Route path="/docs/processes/:slug" element={<DocsProcessPage />} />
          <Route
            path="/docs/processes/:slug/edit"
            element={<DocsProcessEditPage />}
          />
          <Route
            path="/docs/procedures/:slug"
            element={<DocsProcedurePage />}
          />
          <Route
            path="/docs/procedures/:slug/edit"
            element={<DocsProcedureEditPage />}
          />
          {isAdmin && (
            <>
              <Route path="/admin/printers" element={<PrinterAdminPage />} />
              <Route
                path="/docs/admin/review"
                element={<DocsReviewQueuePage />}
              />
              <Route path="/docs/admin/import" element={<DocsImportPage />} />
              <Route path="/docs/admin" element={<DocsAdminPage />} />
            </>
          )}
          <Route path="/signin" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const Router: React.FC = () => {
  const { isLoading, isAuthenticated, signOut, clearanceLevel } = useAuth();
  const isAdmin =
    typeof clearanceLevel === 'number' &&
    clearanceLevel >= ADMIN_CLEARANCE_LEVEL;

  // Show loading screen while auth is initializing
  if (isLoading) {
    return (
      <LoadingSpinner title="Loading UM Tools" message="Authenticating..." />
    );
  }
  return (
    <BrowserRouter>
      {!isAuthenticated ? (
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      ) : (
        <MainContent signOut={signOut} isAdmin={isAdmin} />
      )}
    </BrowserRouter>
  );
};

export default Router;
